import { db } from "services/firebase";

const CADENCE_MS = {
  daily: 24 * 60 * 60 * 1000,
  weekly: 7 * 24 * 60 * 60 * 1000,
  monthly: 30 * 24 * 60 * 60 * 1000,
  quarterly: 90 * 24 * 60 * 60 * 1000,
};

export function computeNextSendAt(cadence, fromDate = new Date()) {
  const offset = CADENCE_MS[cadence] || CADENCE_MS.monthly;
  return new Date(fromDate.getTime() + offset);
}

/**
 * Scheduled delivery is restricted to the creating user's own Auth email.
 * Returns the normalized account email to persist, or throws.
 */
export function assertOwnAccountRecipientEmail({
  recipientEmail,
  accountEmail,
  emailVerified = false,
}) {
  const account = String(accountEmail || "").trim();
  const recipient = String(recipientEmail || "").trim();

  if (!account) {
    throw new Error(
      "Your account email is required to create a delivery schedule.",
    );
  }

  if (!emailVerified) {
    throw new Error(
      "Verify your account email before scheduling report delivery.",
    );
  }

  if (!recipient || recipient.toLowerCase() !== account.toLowerCase()) {
    throw new Error(
      "Scheduled reports can only be delivered to your own account email.",
    );
  }

  // Persist the Auth email exactly so Firestore rules can match token.email.
  return account;
}

export async function listReportSchedulesForUser(uid) {
  if (!uid) return [];
  const snap = await db
    .collection("reportSchedules")
    .where("uid", "==", uid)
    .orderBy("updatedAt", "desc")
    .get();
  return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

export async function createReportSchedule({
  uid,
  institutionId,
  savedReportId,
  cadence,
  recipientEmail,
  accountEmail,
  emailVerified = false,
}) {
  if (!uid || !institutionId || !savedReportId || !cadence) {
    throw new Error("uid, institutionId, savedReportId, and cadence are required.");
  }

  const resolvedEmail = assertOwnAccountRecipientEmail({
    recipientEmail,
    accountEmail,
    emailVerified,
  });

  const now = new Date();
  const nextSendAt = computeNextSendAt(cadence, now);

  const docRef = await db.collection("reportSchedules").add({
    uid,
    institutionId,
    savedReportId,
    cadence,
    enabled: true,
    recipientEmail: resolvedEmail,
    lastSentAt: null,
    nextSendAt,
    createdAt: now,
    updatedAt: now,
  });

  return docRef.id;
}

export async function deleteReportSchedule(scheduleId) {
  if (!scheduleId) return;
  await db.collection("reportSchedules").doc(scheduleId).delete();
}
